/**
 * Application-specific action handlers.
 *
 * Register handlers for data-action and data-key-action attributes.
 */

import { registerHandler } from './delegator.js';

// Sidebar
registerHandler('open-sidebar', () => {
  document.getElementById('sidebar').classList.add('open');
  document.getElementById('sidebar-overlay').classList.add('open');
  document.body.style.overflow = 'hidden';
});

registerHandler('close-sidebar', () => {
  document.getElementById('sidebar').classList.remove('open');
  document.getElementById('sidebar-overlay').classList.remove('open');
  document.body.style.overflow = '';
});

// Welcome popup
registerHandler('dismiss-welcome', () => {
  const popup = document.getElementById('welcome-popup');
  if (!popup) return;

  // Set cookie to prevent showing again (24 hours)
  const expires = new Date();
  expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000);
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = 'cs.welcomeDismissed=true; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax' + secure;

  // Animate out
  popup.style.transform = 'translateY(20px)';
  popup.style.opacity = '0';
  setTimeout(() => {
    popup.style.display = 'none';
  }, 300);
});

/**
 * Initialize welcome popup for anonymous users.
 * Shows after 500ms delay on desktop screens.
 */
function initWelcomePopup() {
  // Skip on mobile screens (< 480px)
  if (window.innerWidth < 480) return;

  // Skip on auth pages (already have sign-in options)
  if (window.location.pathname.startsWith('/auth/')) return;

  // Skip if already signed in (check for session cookie)
  if (document.cookie.indexOf('cs.loggedIn=') !== -1) return;

  // Skip if popup was dismissed
  if (document.cookie.indexOf('cs.welcomeDismissed=') !== -1) return;

  const popup = document.getElementById('welcome-popup');
  if (!popup) return;

  // Show popup after delay
  setTimeout(() => {
    popup.style.display = 'block';
    // Trigger reflow for transition
    popup.offsetHeight;
    popup.style.transform = 'translateY(0)';
    popup.style.opacity = '1';
  }, 500);
}

/**
 * Initialize Google One Tap sign-in with FedCM.
 */
function initOneTap() {
  // Skip if already signed in (access_token is HttpOnly, so check the indicator cookie)
  if (document.cookie.indexOf('cs.loggedIn=') !== -1) return;

  // Get Google client ID from meta tag
  const clientIdMeta = document.querySelector('meta[name="google-client-id"]');
  if (!clientIdMeta) return;

  const clientId = clientIdMeta.getAttribute('content');
  if (!clientId) return;

  // Wait for GSI library to load
  if (typeof google === 'undefined' || !google.accounts) {
    setTimeout(initOneTap, 100);
    return;
  }

  // Check if we've already shown One Tap this session
  if (sessionStorage.getItem('cs.oneTapShown')) return;

  // Initialize GSI with FedCM enabled
  google.accounts.id.initialize({
    client_id: clientId,
    callback: handleOneTapCredential,
    use_fedcm_for_prompt: true,    // Enable FedCM (privacy-preserving)
    auto_select: true,             // Auto-select if single account & previously approved
    cancel_on_tap_outside: true
  });

  // Show the One Tap prompt
  google.accounts.id.prompt((notification) => {
    sessionStorage.setItem('cs.oneTapShown', 'true');

    if (notification.isNotDisplayed()) {
      console.log('One Tap: not displayed -', notification.getNotDisplayedReason());
    } else if (notification.isSkippedMoment()) {
      console.log('One Tap: skipped -', notification.getSkippedReason());
    } else if (notification.isDismissedMoment()) {
      console.log('One Tap: dismissed -', notification.getDismissedReason());
    }
  });
}

/**
 * Handle One Tap credential response.
 */
function handleOneTapCredential(response) {
  console.log('One Tap: Got credential, signing in');

  fetch('/auth/fedcm/google', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'FedCM'  // CSRF protection header
    },
    body: JSON.stringify({
      credential: response.credential,
      redirect: window.location.pathname
    })
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.success) {
        console.log('One Tap: Sign-in successful, reloading');
        window.location.reload();
      } else {
        console.log('One Tap: Server rejected credential -', data.error);
      }
    })
    .catch((error) => {
      console.log('One Tap: Server error -', error.message);
    });
}

// Run initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initWelcomePopup();
    initOneTap();
  });
} else {
  initWelcomePopup();
  initOneTap();
}

// Copy to clipboard
registerHandler('copy-to-clipboard', (el) => {
  const value = el.dataset.copyValue;
  if (!value) return;

  navigator.clipboard.writeText(value).then(() => {
    el.classList.add('copied');
    setTimeout(() => el.classList.remove('copied'), 2000);
  }).catch((err) => {
    console.error('Failed to copy to clipboard:', err);
  });
});

// Tab switching - works with any tab class (*-tab, *-tab-active pattern)
registerHandler('switch-tab', (el) => {
  const tabGroup = el.closest('[data-tab-group]');
  if (!tabGroup) return;

  // Find the tab class pattern from the clicked element (e.g., 'agents-tab' or 'contacts-tab')
  const tabClass = Array.from(el.classList).find((c) => c.endsWith('-tab') && !c.endsWith('-tab-active'));
  if (!tabClass) return;

  const activeClass = tabClass + '-active';

  tabGroup.querySelectorAll('.' + tabClass).forEach((tab) => {
    tab.classList.remove(activeClass);
    tab.setAttribute('aria-selected', 'false');
  });

  el.classList.add(activeClass);
  el.setAttribute('aria-selected', 'true');
});
